import { useMemo, useEffect, useRef, useCallback } from 'react';
import { buildTaskTree } from '../utils/taskUtils';

function computeExpandedSet(tasks, currentFileId) {
  if (tasks.length === 0) return new Set();
  const taskTree = buildTaskTree(tasks);
  const expandedSet = new Set();
  const shouldExpandAllParents = currentFileId === 'preset' || (currentFileId != null && String(currentFileId).startsWith('preset:'));
  const collectExpandedTasks = (taskList) => {
    taskList.forEach(task => {
      if (task.subtasks && task.subtasks.length > 0) {
        const isExpanded = (shouldExpandAllParents && task.status !== 'collapsed') || task.status === 'expanded';
        if (isExpanded) {
          expandedSet.add(task._reactKey);
        }
        collectExpandedTasks(task.subtasks);
      }
    });
  };
  collectExpandedTasks(taskTree);
  return expandedSet;
}

/**
 * Hook for managing task expansion/collapse state.
 * Expansion is derived from task status so the first paint has correct state (e.g. when switching lists).
 */
export function useTaskExpansion({
  tasks,
  currentFileId,
  updateTaskStatus
}) {
  const expandedTasks = useMemo(
    () => computeExpandedSet(tasks, currentFileId),
    [tasks, currentFileId]
  );
  const isInitialLoadRef = useRef(true);

  // NOTE:
  // We intentionally no longer auto-reset parent task status from 'expanded' / 'collapsed'
  // back to 'incomplete' when it appears to lose all subtasks.
  //
  // When creating new parent + subtask pairs (for example via the placeholder row's
  // "Add subtask" button), tasks are first created locally with temporary IDs and then
  // synced to the server. During that sync, there can be brief moments where the parent
  // exists in `tasks` but its subtask relationship has not yet been re-established
  // (e.g. IDs are being remapped from temp_* to DB IDs). The previous effect would see
  // "no subtasks" and eagerly reset the parent's status to 'incomplete', which in turn
  // collapses an expanded parent shortly after creation for logged-in users.
  //
  // Since `computeExpandedSet` already ignores tasks that have no subtasks, keeping a
  // stale 'expanded' / 'collapsed' status on leaf tasks has no impact on UI behavior,
  // so we prefer correctness (no surprise collapsing) over aggressively normalizing
  // the status field here.
  useEffect(() => {
    if (tasks.length === 0 || isInitialLoadRef.current) {
      if (tasks.length > 0) isInitialLoadRef.current = false;
    }
  }, [tasks]);

  /** Apply an updater to the expanded set by syncing task statuses; used by keyboard nav etc. */
  const setExpandedTasks = useCallback((updater) => {
    const currentSet = computeExpandedSet(tasks, currentFileId);
    const newSet = updater(currentSet);
    tasks.forEach(task => {
      const hasSubtasks = tasks.some(t => t.parent_task_id === task.id);
      if (!hasSubtasks) return;
      const shouldBeExpanded = newSet.has(task._reactKey);
      const isCurrentlyExpanded = currentSet.has(task._reactKey);
      if (shouldBeExpanded !== isCurrentlyExpanded) {
        updateTaskStatus(task.id, shouldBeExpanded ? 'expanded' : 'collapsed');
      }
    });
  }, [tasks, currentFileId, updateTaskStatus]);

  const toggleExpand = useCallback((clientId) => {
    const task = tasks.find(t => t._reactKey === clientId);
    if (!task) return;
    const hasSubtasks = tasks.some(t => t.parent_task_id === task.id);
    if (!hasSubtasks) return;
    const isCurrentlyExpanded = expandedTasks.has(clientId);
    updateTaskStatus(task.id, isCurrentlyExpanded ? 'collapsed' : 'expanded');
  }, [tasks, expandedTasks, updateTaskStatus]);

  return {
    expandedTasks,
    setExpandedTasks,
    toggleExpand
  };
}
